// Opus encode and decode shared by every voice backend: WebCodecs
// `AudioEncoder` for the mic, the `opus-decoder` package for playback.

const NOOP = (_?: unknown) => {
  /* intentionally empty */
}
const DEBUG_AUDIO = true

export type DecoderWrapper = {
  raw: any
  decode: (frame: Uint8Array) => any
}

export type OpusWebCodecs = {
  encoder: any
  close: () => Promise<void>
}

export async function createOpusDecoder(sampleRate: number, numChannels: number): Promise<DecoderWrapper> {
  const mod: any = await import('opus-decoder')
  const Ctor = mod?.default ?? mod?.OpusDecoder ?? mod?.Decoder ?? mod
  const inst = typeof Ctor === 'function' ? new Ctor({ sampleRate, channels: numChannels }) : Ctor
  if (inst?.ready) await inst.ready

  const methods = Object.keys(inst || {})
  if (DEBUG_AUDIO) console.log('[AUDIO] decoder methods', { sampleRate, numChannels, methods })

  const decode =
    typeof inst?.decode === 'function'
      ? (frame: Uint8Array) => inst.decode(frame)
      : typeof inst?.decodeFrame === 'function'
      ? (frame: Uint8Array) => inst.decodeFrame(frame)
      : typeof inst?.decodePacket === 'function'
      ? (frame: Uint8Array) => inst.decodePacket(frame)
      : typeof inst?.decodeFrames === 'function'
      ? (frame: Uint8Array) => inst.decodeFrames([frame])
      : null

  if (!decode) throw new Error('Unsupported opus-decoder API: no decode/decodeFrame/decodePacket/decodeFrames found')
  return { raw: inst, decode }
}

export async function createWebCodecsOpusEncoder(args: {
  sampleRate: number
  numberOfChannels: number
  bitrate?: number
  onOpus: (opusFrame: Uint8Array) => void
}): Promise<OpusWebCodecs> {
  const { sampleRate, numberOfChannels, bitrate = 24000, onOpus } = args

  const AnyAudioEncoder = (window as any).AudioEncoder
  if (!AnyAudioEncoder) throw new Error('WebCodecs AudioEncoder not available in this browser')

  const encoder = new AnyAudioEncoder({
    output: async (chunk: any) => {
      try {
        const ab = new ArrayBuffer(chunk.byteLength)
        chunk.copyTo(new Uint8Array(ab))
        if (DEBUG_AUDIO)
          console.log('[TX] encoder output', { bytes: chunk.byteLength, type: chunk.type, ts: chunk.timestamp })
        onOpus(new Uint8Array(ab))
      } catch (err) {
        NOOP(err)
      }
    },
    error: (e: any) => {
      console.error('[TX] AudioEncoder error', e)
    },
  })

  encoder.configure({
    codec: 'opus',
    sampleRate,
    numberOfChannels,
    bitrate,
    latencyMode: 'realtime',
  })

  const close = async () => {
    try {
      await encoder.flush()
    } catch (err) {
      NOOP(err)
    }
    try {
      encoder.close()
    } catch (err) {
      NOOP(err)
    }
  }

  return { encoder, close }
}
